import { ForwardToInbox } from '@mui/icons-material';
import {
  Box,
  IconButton,
  styled,
  Typography,
  type CSSObject,
  type Theme,
  useMediaQuery,
} from '@mui/material';
import { setBtnVisibility } from '@rumblefish/ui/redux/reducers/telegramBtn';
import { Layout, Contact } from '@rumblefish/ui/Rumblefish23Theme';
import { Section } from '@rumblefish/ui/Rumblefish23Theme';
import { useInView } from 'framer-motion';
import { useRouter } from 'next/router';
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { handleScrollTop, handleScroll } from '../../utils/handleScroll';
import { NextHeadWithMeta } from '../NextHeadWithMeta';
import { caseStudiesSeo } from './CaseStudies.seo';
import type { CaseStudiesProps, QueryType } from './CaseStudies.types';
import { formatFilterNameToValue } from './CaseStudies.utils';
import { FiltersContainer } from './FiltersContainer';

const IntroHeading = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  fontFamily: 'ClashDisplay',
  fontSize: '72px',
  fontWeight: 900,
  lineHeight: '1.1',
  letterSpacing: '-0.02em',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(3),
}));

const IntroSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.SECONDARY,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '1.6',
  maxWidth: '600px',
}));

const ScrollToContactButton = styled(IconButton)<{ showButton?: boolean }>(
  ({ showButton = true, theme }) => ({
    display: 'none',
    position: 'fixed',
    bottom: '18px',
    right: '24px',
    color: theme.palette.invertedContent?.PRIMARY,
    backgroundColor: theme.palette.raspberryColors?._0,
    transitionProperty: 'opacity, z-index',
    transitionDuration: '0.2s',
    transition: 'ease',
    opacity: showButton ? 1 : 0,
    zIndex: showButton ? 150 : -1,

    '&:hover': {
      backgroundColor: theme.palette.raspberryColors?.PLUS_2,
    },
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
  })
);

export const CaseStudies = ({
  caseStudies,
  blogPosts,
  caseStudyServices,
}: CaseStudiesProps) => {
  const router = useRouter();

  const dispatch = useDispatch();

  const contactEl = useRef<HTMLDivElement>(null);

  const studiesEl = useRef<HTMLDivElement>(null);

  const isContactVisible = useInView(contactEl);

  const isStudiesVisible = useInView(studiesEl);

  const [showSwipeIcon, setShowSwipeIcon] = useState(true);

  const [hideFilters, setHideFilters] = useState(false);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const usedServiceNames = useMemo(
    () =>
      new Set(
        caseStudies.flatMap((cs) =>
          cs.case_study_services.map((s) => s?.name).filter(Boolean)
        )
      ),
    [caseStudies]
  );

  const serviceFilters = useMemo(
    () =>
      caseStudyServices
        .filter((service) => usedServiceNames.has(service.name))
        .map((service) => ({
          value: formatFilterNameToValue(service.name),
          label: service.name,
        })),
    [caseStudyServices, usedServiceNames]
  );

  const shallowRedirect = useCallback(
    (query: QueryType) => router.push({ query }, undefined, { shallow: true }),
    [router]
  );

  const handleScrollToContact = useCallback(() => {
    handleScroll(contactEl);
  }, [contactEl]);

  const page = Number(router.query.page || 1);

  const notOnCaseStudiesRootRoute = router.query.page !== undefined;

  const filter = router.query.filter
    ? (router.query.filter as string).trim().replace(/\s/g, '-').toLowerCase()
    : '';

  useEffect(() => {
    notOnCaseStudiesRootRoute && handleScrollTop();
  }, [page]);

  useEffect(() => {
    const telegramBtnVisibilityInterval = setTimeout(() => {
      dispatch(setBtnVisibility(isContactVisible || !isStudiesVisible));
    }, 200);

    setShowSwipeIcon(!isContactVisible && isStudiesVisible);
    setHideFilters(isContactVisible || !isStudiesVisible);

    return () => clearInterval(telegramBtnVisibilityInterval);
  }, [isContactVisible, isStudiesVisible, mdDown]);

  return (
    <Layout blogPosts={blogPosts}>
      <NextHeadWithMeta {...caseStudiesSeo} />
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Section width="normal">
          <Box sx={{ pt: 10, pb: 6 }}>
            <IntroHeading variant="h1">Case Studies</IntroHeading>
            <IntroSubtitle variant="body1">
              Real products, real impact. Explore how we&apos;ve helped our
              clients build and scale software across blockchain, DeFi, fintech,
              and beyond.
            </IntroSubtitle>
          </Box>
        </Section>
      </Box>
      <FiltersContainer
        presentFilter={filter}
        pagination={page}
        filters={serviceFilters}
        shallowRedirect={shallowRedirect}
        data={caseStudies}
        showSwipeIcon={showSwipeIcon}
        caseStudiesGridEl={studiesEl as RefObject<Element | DocumentFragment>}
        hideFilters={mdDown ? hideFilters : false}
      />
      <ScrollToContactButton
        showButton={showSwipeIcon}
        onClick={handleScrollToContact}>
        <ForwardToInbox />
      </ScrollToContactButton>
      <Box id="case-studies-grid" ref={studiesEl} />
      <Box ref={contactEl} />
      <Contact color="raspberry" />
    </Layout>
  );
};
